/**
 * The upload batch, held outside any screen.
 *
 * It used to live in the Upload page's state, which meant leaving the page
 * forgot the batch. Here the uploads carry on whatever screen is open, and the
 * batch is still there to come back to. (Memory only: a reload ends it, as
 * closing a tab mid-upload would.)
 *
 * **Upload goes through the service** (constitution §4): the service decides
 * the type by the bytes' signature, checks the size, the storage limit and the
 * duplicate, and hashes what it receives — so every check here is a courtesy
 * that saves a round trip, and the service's answer is the one that counts.
 *
 * A file's way through (plan D7, D8):
 *
 * 1. **checking** — hashed in the browser (`crypto.subtle`) and looked up with
 *    `GET /documents/duplicates?sha256=`. A twin in sight means the person is
 *    **asked** before a byte is sent; nothing is ever silently merged.
 * 2. **asking** — the duplicate choice: *keep both*, *link as a new version*,
 *    or *discard* (which stores nothing and audits the choice).
 * 3. **queued** → **uploading** — sent in batches of at most
 *    `max_files_per_request` files and 48 MiB, because the edge bounds the
 *    whole request at 52 MB; a file larger than that travels alone.
 * 4. **done**, **refused** (with the service's reason — corrupt, not a stored
 *    type, too large, the storage limit), **error** (worth trying again) or
 *    **discarded**.
 *
 * If the service still finds a twin the pre-hash did not (`CONFLICT`,
 * `reason: duplicate`), the file goes back to **asking** with the service's
 * list — nothing was stored.
 *
 * **A dropped folder keeps its structure** (FR-7, W5): its folders are made
 * once, under the target folder, by one `POST /folders/paths` for the whole
 * drop (idempotent, so a second drop of the same tree makes nothing new), and
 * each file joins the queue filed in its own folder — then goes the same way as
 * any other file, its own states and all. A drop of more than 200 files is
 * refused whole, before anything is made or sent.
 *
 * **A host may name the folder path** (FR-39, D45): a sibling's batch then
 * carries `folder_path` — under its `folderId` when it gives one — and the
 * service makes the missing folders once, kept by the host's app. A folder
 * dropped there makes nothing first: each file's path is the host's path and
 * then the folders it sat in, and the service makes them as it files it.
 *
 * Progress: `fetch` cannot report upload progress, so it is animated while the
 * request is out.
 *
 * **A host may ask for each file to be read** (engine W15, FR-E40, T15.15):
 * the batch then carries a `parse` form field, and the service creates each
 * stored version's parse job **in the same transaction** as the version — so
 * a refused reading (the profile, the kind of file, the documents-processed
 * limit, parsing not offered) **stores nothing**, and the file's row says why.
 * Each stored file's answer carries its job, followed to the end beside the
 * file: read, waiting for review, or refused with its reason. Without a
 * `parse` request nothing is ever asked of the engine and no field is sent:
 * the default stays store-only (ADR-0013).
 *
 * **W22 (FR-55, D53): a stored file says what happened in five words** —
 * *Stored*, *Reading*, *Needs review*, *Accepted*, *Failed* (`readingOfItem`),
 * each from the job's status and its extraction's review, followed past the
 * job's end until the review says where it stands. A reading that needs a
 * password is **unlocked from the row** (`unlockItem`): the same parse asked
 * again with the typed password, used once and kept nowhere by the service; a
 * reading that completes leaves the password in this tab's memory for the
 * pages (`password-keeper.ts`), a wrong one is dropped. A failed reading is
 * **uploaded again** (`uploadAgain`): the new file stored as the next version
 * of the same document, read as the first was asked.
 *
 * **W22 (FR-59, D57): the batch outlives the popup.** `uploadsInFlight` counts
 * the files not yet stored for the leave warning and the org switch's
 * question; `trayOf` is the tray's line; `stopUploads` drops what has not been
 * sent.
 *
 * **W30 (FR-67, ADR-0021, D103): a protected PDF's password, at upload, per
 * file.** As it is checked, each PDF is opened by pdf.js in this tab
 * (`AddOptions.openPdf`); one that needs a password **waits** (`lock`) for the
 * person: *Check* tries the typed password with pdf.js — it opens, and goes
 * with the file; or *That password doesn't open this file* — and *Skip* stores
 * it locked, as before. A checked password travels **with its file alone** (the
 * form has one `password` field), the service checks it again before it keeps
 * it sealed, and the row says what the service did: saved, or not and why — a
 * wrong one still stores the file. The password is held in this module's
 * memory only while the file waits to be sent (`secrets`, never on the item a
 * screen renders), and dropped once the file is stored, refused, removed or
 * stopped. Never browser storage, never the console.
 */
import { useSyncExternalStore } from 'react'

import type { PdfOpener } from './evidence-page'
import { jobEnded, jobReasonText, type ParseJob, type ParseOnUpload, type ReadingState } from './extraction-model'
import { JOB_POLL_MS, followJob, nextPollDelay } from './jobs'
import { passwordKeeper } from './password-keeper'

import {
  ApiError,
  DEFAULT_MAX_FILES_PER_REQUEST,
  bytesOf,
  duplicatesNamed,
  formatBytes,
  mediaTypeOf,
  pairRefusalOf,
  sha256Hex,
  type OnDuplicate,
  type PairRefusal,
  type ProDocClient,
  type ProDocDocument,
} from './prodoc-client'
import { errorText } from './hooks'

export type UploadState = 'checking' | 'asking' | 'queued' | 'uploading' | 'done' | 'refused' | 'error' | 'discarded'

/**
 * What a host asks the engine to read each stored file as (FR-E40): a profile
 * — the document type's code, or `auto` — the review (`auto`, ProDoc's; or
 * `app`, the host's own for its own documents), and the profile's options and
 * the context it reads against (FR-E7). Sent as the upload's `parse` field.
 */
export type UploadParseRequest = ParseOnUpload

/** Where a stored file's reading stands: the job's own status — or `error` when the answer named no job or following it failed. */
export interface ParseProgress {
  state: 'queued' | 'running' | 'completed' | 'refused' | 'failed' | 'error'
  job?: ParseJob
  /** Once read: its review's state — `accepted`, `needs_review`, `app_review`, `rejected`; `pending` while it is being routed. */
  review?: string | null
  reconciled?: boolean
  /** Why it was not read — the job's reason, or the request's refusal. */
  message?: string
  code?: string
}

/** An earlier document with the same bytes — whole when the lookup returned it, an id when only a refusal named it. */
export type Twin = ProDocDocument | { id: string }

/**
 * W30 (FR-67, D103): an encrypted PDF's password at upload — `asking`: the box
 * is shown and the file waits (`wrong` after a password that did not open it);
 * `checking`: pdf.js is trying the typed one; `opens`: it opened the file and
 * goes with it (`unchecked` when this browser could not try it, so the service
 * alone checks it); `skipped`: stored locked, as before. The password itself is
 * never here.
 */
export type UploadLock = { state: 'asking'; wrong?: boolean } | { state: 'checking' } | { state: 'opens'; unchecked?: boolean } | { state: 'skipped' }

export interface UploadItem {
  key: string
  file: File
  state: UploadState
  /** 0–100 while uploading. */
  progress: number
  /** Why it was refused or failed to upload — the service's own sentence. */
  message?: string
  /** The service's machine code for a failure — `USAGE_LIMIT_EXCEEDED` is a plan's limit, named by `limit`. */
  code?: string
  /** The service's `details.reason`: `corrupt`, `unsupported_type`, `too_large`, `duplicate`, … */
  reason?: string
  /** With `USAGE_LIMIT_EXCEEDED`, which limit (`details.limit`): the storage limit, or `documents_processed` for a reading (T15.15). */
  limit?: string
  /** The refusal was of the reading the upload asked for (T15.15) — and so nothing was stored. */
  parseRefused?: boolean
  /** A sibling's pair refused the batch (FR-33): not connected, and why — never the paywall. */
  pair?: PairRefusal
  /** The browser's hash of the file, once computed. */
  sha256?: string
  /** What the file's name says it is — compared with what the bytes turned out to be (FR-2). */
  expectedType: string | null
  /** Live documents with these bytes, while the person decides. */
  twins: Twin[]
  /** What the person chose for a duplicate. */
  choice?: OnDuplicate | 'discard'
  /** For *link as a new version*: the document the bytes join. */
  linkTo?: string
  /** Once the service has it — for a new version, the document it joined. */
  document?: ProDocDocument
  /** Whose batch: a batch never follows somebody to another organisation. */
  owner: string
  workspaceId: string | null
  /** W5: the folder it is filed in — the target, or the folder a dropped tree made for it. */
  folderId: string | null
  /** W9 (FR-39): the folder path the host named, below `folderId` — sent as `folder_path`; the service makes what is missing. */
  folderPath?: string[]
  /** For a file from a dropped folder: where it sat in the drop, `a/b/x.pdf`. */
  relativePath?: string
  documentType: string | null
  /** Engine W15 (FR-E40): the reading the host asked for, once the file is stored. Absent when none was asked. */
  parse?: ParseProgress
  /** W22: the app whose record it is — the tray and the leave warning count a host's own files. */
  ownerApp: string
  /** W22 (FR-55, D53): *Upload again* — its next version on its way, or refused and why. The stored document is unchanged until it lands. */
  again?: { state: 'uploading'; fileName: string } | { state: 'refused'; fileName: string; message: string }
  /** W30 (FR-67): the file needs a password — the box, the check, or *Skip*. Absent for a file that needs none (or one this browser could not open). */
  lock?: UploadLock
  /** W30: what the service did with the password sent with it — kept sealed, or not. Absent when none was sent. */
  passwordSaved?: boolean
  /** W30: why it was not kept (`password_incorrect`, `not_encrypted`, `password_unsupported`); the file is stored all the same. */
  passwordError?: string | null
}

export interface AddOptions {
  /** The org's accepted media types and ceiling — served by the API, never hard-coded. */
  mediaTypes: string[]
  maxBytes: number
  /** D8's cap per request, from `/documents/options`. */
  maxFiles?: number
  /** Omitted: the organisation's default workspace (D9). */
  workspaceId: string | null
  /** W5: a folder of that workspace; omitted, its top level. */
  folderId?: string | null
  /** W9 (FR-39, D45): the host's folder path below `folderId`, as names — a sibling's `ownerApp` only. */
  folderPath?: string[] | null
  /** A code from the enabled list; omitted, `other`. */
  documentType: string | null
  ownerApp: string
  sourceRef: string | null
  /** The batch's key — the org id, so a switch shows a fresh list. */
  owner: string
  /** Engine W15 (FR-E40): read each stored file as this. Omitted, nothing is parsed. */
  parse?: UploadParseRequest | null
  /** How soon a parse job is first asked about again. */
  jobPollMs?: number
  /**
   * W30 (FR-67, D103): how a picked PDF is opened in this tab to see whether it
   * needs a password, and to check one — pdf.js unless a test says otherwise.
   * Omitted, no PDF is opened here and none waits for a password.
   */
  openPdf?: PdfOpener | null
}

/** The edge allows 52 MB per request (spec §7); a batch stays under 48 MiB so the multipart framing fits (D8). */
export const MAX_BATCH_BYTES = 48 * 1024 * 1024
/** Requests in flight at once. More only queues on the server. */
const CONCURRENCY = 2
/** Files hashed at once — hashing reads the whole file into memory. */
const CHECKING = 2

interface Pending {
  client: ProDocClient
  options: AddOptions
}

let items: UploadItem[] = []
const pending = new Map<string, Pending>()
/** W30: a checked password, by file, until its file is sent — memory only, never on the item a screen renders. */
const secrets = new Map<string, string>()
/** Files whose check has started, so a second pump cannot start it twice. */
const started = new Set<string>()
const listeners = new Set<() => void>()
let counter = 0
let checking = 0
let sending = 0

function emit() {
  for (const l of listeners) l()
}

function patch(key: string, change: Partial<UploadItem>) {
  items = items.map((i) => (i.key === key ? { ...i, ...change } : i))
  emit()
}

const find = (key: string) => items.find((i) => i.key === key)

/* ------------------------------------------------------------- batches */

/** What a batch's files must share: one request carries one set of fields (D8). */
export interface Batchable {
  key: string
  size: number
  /** Files with the same group may travel together. */
  group: string
  /** A file that must travel alone — a new version names one document. */
  alone?: boolean
}

/**
 * Split the queue into requests: in order, within a group, at most `maxFiles`
 * files and `maxBytes` bytes each. A file larger than `maxBytes`, or one that
 * must travel alone, is a request of its own.
 */
export function planBatches(queue: Batchable[], limits: { maxFiles: number; maxBytes: number }): string[][] {
  const maxFiles = Math.max(1, limits.maxFiles)
  const open = new Map<string, { keys: string[]; bytes: number }>()
  const batches: { keys: string[]; bytes: number }[] = []
  for (const item of queue) {
    if (item.alone || item.size > limits.maxBytes) {
      batches.push({ keys: [item.key], bytes: item.size })
      continue
    }
    const current = open.get(item.group)
    if (current && current.keys.length < maxFiles && current.bytes + item.size <= limits.maxBytes) {
      current.keys.push(item.key)
      current.bytes += item.size
      continue
    }
    const next = { keys: [item.key], bytes: item.size }
    open.set(item.group, next)
    batches.push(next)
  }
  return batches.map((b) => b.keys)
}

function groupOf(item: UploadItem, options: AddOptions): string {
  return JSON.stringify([
    options.ownerApp,
    options.sourceRef,
    item.workspaceId,
    item.folderId,
    item.folderPath ?? null,
    item.documentType,
    item.choice === 'keep_both' ? 'keep_both' : null,
    // Files read as different things travel apart, so each request's answer is matched to its own ask.
    options.parse ?? null,
  ])
}

/* -------------------------------------------------------------- checking */

/** Whether pdf.js says this PDF needs a password (W30). Anything else — it opened, or this browser cannot open it — is no: the service sniffs it either way. */
async function needsPassword(item: UploadItem, p: Pending): Promise<boolean> {
  const open = p.options.openPdf
  if (!open || item.expectedType !== 'application/pdf') return false
  try {
    const pdf = await open(new Uint8Array(await bytesOf(item.file)))
    pdf.destroy()
    return false
  } catch (e) {
    return refusalReason(e) === 'encrypted'
  }
}

/** A `PdfRefusal`'s reason, read by shape so the queue holds no page code. */
const refusalReason = (e: unknown): string | null => (e instanceof Error && e.name === 'PdfRefusal' ? String((e as { reason?: unknown }).reason ?? '') : null)

async function check(item: UploadItem, p: Pending) {
  checking += 1
  try {
    const sha256 = await sha256Hex(item.file)
    patch(item.key, { sha256 })
    // W30 (FR-67): a PDF that needs a password waits for it — or for *Skip* — before it is sent.
    if (await needsPassword(item, p)) patch(item.key, { lock: { state: 'asking' } })
    const twins = (await p.client.documents.duplicates(sha256)).filter((d) => d.status === 'stored')
    if (twins.length > 0) patch(item.key, { state: 'asking', twins })
    else patch(item.key, { state: 'queued' })
  } catch {
    // The look-up is a courtesy (D7): the service hashes what it receives and
    // refuses a duplicate itself, so a failed look-up never blocks an upload.
    if (find(item.key)?.state === 'checking') patch(item.key, { state: 'queued' })
  } finally {
    checking -= 1
    pump()
  }
}

/* --------------------------------------------------------------- sending */

/** The limit the engine's reading counts against (FR-E35) — the service's `documents_processed`. */
export const READING_LIMIT = 'documents_processed'

/**
 * A refusal of the reading an upload asked for (T15.15): a kind of file the
 * profile cannot read, the documents-processed limit, parsing not offered, a
 * profile the organisation does not parse with, or a `parse` object the
 * service would not take. The service checks the reading with the version, so
 * any of these means **nothing was stored**.
 */
export function isParseRefusal(e: unknown): boolean {
  if (!(e instanceof ApiError)) return false
  if (e.code === 'PROFILE_MEDIA_MISMATCH') return true
  if (e.code === 'USAGE_LIMIT_EXCEEDED') return e.details.limit === READING_LIMIT
  if (e.reason === 'parsing_not_offered' || e.reason === 'unknown_profile') return true
  const field = e.details.field
  return typeof field === 'string' && (field === 'parse' || field.startsWith('parse.'))
}

/** Where a refusal leaves a file: asked again, refused for good, or worth another try. */
function refusalChange(e: unknown): Partial<UploadItem> {
  if (e instanceof ApiError && e.reason === 'duplicate') {
    // Nothing was stored: the service found a twin. Ask, with its list.
    return { state: 'asking', twins: duplicatesNamed(e).map((id) => ({ id })), choice: undefined, linkTo: undefined, progress: 0 }
  }
  const parseRefused = isParseRefusal(e)
  const limit = e instanceof ApiError && typeof e.details.limit === 'string' ? e.details.limit : undefined
  const base = {
    message: `${errorText(e, 'The upload did not complete.')}${parseRefused ? ' Nothing was stored — a file asked to be read goes in only with its reading.' : ''}`,
    code: e instanceof ApiError ? e.code : undefined,
    reason: e instanceof ApiError ? e.reason : undefined,
    limit,
    parseRefused,
    pair: pairRefusalOf(e) ?? undefined,
  }
  // A pair the platform could not confirm (fail closed, FR-33) is worth another try; one that is off is not.
  const transient =
    !(e instanceof ApiError) || e.status === 0 || e.status >= 500 || e.code === 'INTERNAL' || e.code === 'STORAGE_UNAVAILABLE' || base.pair?.reason === 'authority_unreachable'
  return { ...base, state: transient ? 'error' : 'refused' }
}

async function send(keys: string[]) {
  const batch = keys.map(find).filter((i): i is UploadItem => Boolean(i))
  const first = batch[0]
  const p = first ? pending.get(first.key) : undefined
  if (!first || !p) return
  sending += 1
  for (const item of batch) patch(item.key, { state: 'uploading', progress: 4, message: undefined, code: undefined, reason: undefined, limit: undefined, parseRefused: undefined, pair: undefined })
  const timer = setInterval(() => {
    for (const item of batch) {
      const current = find(item.key)
      if (current?.state === 'uploading') patch(item.key, { progress: Math.min(90, current.progress + 9) })
    }
  }, 150)
  try {
    const result = await p.client.documents.upload({
      files: batch.map((i) => i.file),
      workspace_id: first.workspaceId,
      document_type: first.documentType ?? undefined,
      owner_app: p.options.ownerApp,
      source_ref: p.options.sourceRef,
      on_duplicate: first.choice === 'keep_both' || first.choice === 'new_version' ? first.choice : undefined,
      duplicate_of: first.choice === 'new_version' ? first.linkTo : undefined,
      // A new version joins its document where it is; only a new document is filed.
      folder_id: first.choice === 'new_version' ? undefined : (first.folderId ?? undefined),
      folder_path: first.choice === 'new_version' ? undefined : first.folderPath,
      // FR-E40, T15.15: read only when the host asked — never because a file arrived (FR-E1). The
      // batch's files share it (the group key holds it), and each stored version gets its own job.
      parse: p.options.parse ?? undefined,
      // W30 (FR-67): a checked password travels with its file alone (`planBatches`' `alone`).
      password: batch.length === 1 ? (secrets.get(first.key) ?? undefined) : undefined,
    })
    batch.forEach((item, i) => {
      const outcome = result.results.find((r) => r.index === i) ?? result.results[i]
      const sent = batch.length === 1 && secrets.has(item.key)
      if (!outcome) patch(item.key, { state: 'error', message: 'The service did not answer for this file.' })
      else if (outcome.ok) {
        const asked = Boolean(p.options.parse)
        secrets.delete(item.key)
        patch(item.key, {
          state: 'done',
          progress: 100,
          document: outcome.document,
          parse: !asked ? undefined : outcome.job ? { state: progressOf(outcome.job), job: outcome.job } : { state: 'error', message: 'It was stored, but ProDoc did not say it would read it.' },
          // W30: what the service did with the password — said on the row; nothing said is not saved.
          ...(sent ? { passwordSaved: outcome.password_saved === true, passwordError: outcome.password_error ?? null } : {}),
        })
        if (asked && outcome.job) void followParse(item.key, outcome.document, outcome.job, p)
      } else {
        const change = refusalChange(outcome.error)
        if (change.state === 'refused') secrets.delete(item.key)
        patch(item.key, change)
      }
    })
  } catch (e) {
    // A refusal of the whole request — the file cap, an unknown workspace, a
    // lapse — or no answer at all: every file in it shares the outcome.
    const change = refusalChange(e)
    for (const item of batch) {
      // A password is kept only for a file that will be sent again — tried again, or asked about a twin.
      if (change.state === 'refused') secrets.delete(item.key)
      patch(item.key, change)
    }
  } finally {
    clearInterval(timer)
    sending -= 1
    pump()
  }
}

/* --------------------------------------------------------------- parsing */

const progressOf = (job: ParseJob): ParseProgress['state'] =>
  job.status === 'queued' || job.status === 'running' || job.status === 'completed' || job.status === 'refused' || job.status === 'failed' ? job.status : 'queued'

/** How many times a completed reading's review is asked about while it is still being routed (D53). */
const REVIEW_FOLLOW = 6

const pause = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * Follow a stored file's parse job — made with its version (T15.15) — to the
 * end (FR-E9), and say how it ended on the file's row: read, and how its review
 * stands; or refused or failed, and why. The file is stored either way.
 *
 * W22 (D53): a completed job is followed on to its extraction's review — asked
 * again while it is still `pending` (being routed) — so the row's word is
 * *Accepted* or *Needs review*, never a guess. With `password` (an unlock), a
 * reading that completes keeps it in this tab's memory for the pages; one that
 * does not, keeps nothing.
 */
async function followParse(key: string, doc: ProDocDocument, start: ParseJob, p: Pending, password?: string) {
  try {
    const job = await followJob(p.client, start, {
      pollMs: p.options.jobPollMs,
      onUpdate: (next) => patch(key, { parse: { state: progressOf(next), job: next } }),
    })
    if (job.status === 'completed') {
      if (password) passwordKeeper.keep(job.version_id, password)
      patch(key, { parse: { state: 'completed', job, review: null } })
      // Its review — accepted, or waiting for a person — from the record, which carries no content.
      for (let attempt = 0; job.extraction_id; attempt += 1) {
        const record = (await p.client.parsing.extractions(doc.id).catch(() => [])).find((r) => r.id === job.extraction_id)
        const review = record?.review?.state ?? null
        if (record) patch(key, { parse: { state: 'completed', job, review, reconciled: record.reconciled } })
        if ((record && review !== 'pending') || attempt + 1 >= REVIEW_FOLLOW || !find(key)) break
        await pause(nextPollDelay(p.options.jobPollMs ?? JOB_POLL_MS, attempt))
      }
    } else if (jobEnded(job)) {
      patch(key, { parse: { state: progressOf(job), job, message: jobReasonText(job.reason) } })
    }
  } catch (e) {
    patch(key, { parse: { state: 'error', message: errorText(e, 'ProDoc did not say how the reading went.'), code: e instanceof ApiError ? e.code : undefined } })
  }
}

/* ---------------------------------------------------- W22: the five words */

/** A reading that needs its password — the row offers the box and *Unlock*. */
export const PASSWORD_REASONS: readonly string[] = ['password_required', 'password_incorrect']

/**
 * A stored file's reading as one of FR-55's five words (D53) — from the job's
 * status and its extraction's review, both the service's: `none` (nothing was
 * asked), `reading`, `needs_review` (`app_review` its app's), `accepted`,
 * `failed` (refused, failed, rejected — `reason` says which). `null` for a
 * file not stored (it keeps its upload's own word).
 */
export function readingOfItem(item: UploadItem): { state: ReadingState; reason: string | null } | null {
  if (item.state !== 'done' || !item.document) return null
  const parse = item.parse
  if (!parse) return { state: 'none', reason: null }
  switch (parse.state) {
    case 'queued':
    case 'running':
      return { state: 'reading', reason: null }
    case 'completed':
      if (parse.review === 'accepted') return { state: 'accepted', reason: null }
      if (parse.review === 'needs_review') return { state: 'needs_review', reason: null }
      if (parse.review === 'app_review') return { state: 'needs_review', reason: 'app_review' }
      if (parse.review === 'rejected') return { state: 'failed', reason: 'rejected' }
      // Read, and its review still being routed: not yet one of the other words.
      return { state: 'reading', reason: null }
    case 'refused':
    case 'failed':
      return { state: 'failed', reason: parse.job?.reason ?? null }
    case 'error':
      return { state: 'failed', reason: parse.code ?? null }
  }
}

/**
 * *Unlock* (FR-55, D53): the same parse asked again for the stored version,
 * with the typed password — used once by the engine and kept nowhere by the
 * service (FR-E6). A failed job is no repeat, so this is a new job; the row
 * follows it, and a wrong password leaves it *Failed* again.
 */
export async function unlockItem(key: string, password: string): Promise<void> {
  const item = find(key)
  const p = pending.get(key)
  const asked = p?.options.parse
  if (!item || !item.document || !p || !asked || !password) return
  const doc = item.document
  patch(key, { parse: { state: 'queued', job: item.parse?.job } })
  try {
    const answer = await p.client.parsing.parse(doc.id, {
      profile: asked.profile,
      version_id: item.parse?.job?.version_id || doc.current_version_id,
      review: asked.review,
      options: asked.options,
      context: asked.context,
      password,
    })
    patch(key, { parse: { state: progressOf(answer.job), job: answer.job } })
    await followParse(key, doc, answer.job, p, password)
  } catch (e) {
    patch(key, { parse: { state: 'error', message: errorText(e, 'ProDoc did not take the password.'), code: e instanceof ApiError ? e.code : undefined } })
  }
}

/* ------------------------------------------- W30: a password at upload */

/** A file waiting for its password — the box is shown, or a typed one is being tried — is not sent. */
const held = (i: UploadItem) => i.lock?.state === 'asking' || i.lock?.state === 'checking'

/**
 * *Check* (FR-67, D103): try the typed password with pdf.js, in this tab. It
 * opens the file → it goes with it, and the file is sent (the service checks it
 * again before it keeps it sealed); it does not → *That password doesn't open
 * this file*, and nothing is kept. A browser that cannot try it lets it go with
 * the file unchecked — the service's check is the one that counts.
 */
export async function checkPassword(key: string, password: string): Promise<void> {
  const item = find(key)
  const p = pending.get(key)
  if (!item || !p || !password || item.lock?.state !== 'asking') return
  patch(key, { lock: { state: 'checking' } })
  let unchecked = false
  try {
    const open = p.options.openPdf
    if (!open) unchecked = true
    else {
      const pdf = await open(new Uint8Array(await bytesOf(item.file)), { password })
      pdf.destroy()
    }
  } catch (e) {
    const reason = refusalReason(e)
    if (reason === 'wrong_password' || reason === 'encrypted') {
      if (find(key)) patch(key, { lock: { state: 'asking', wrong: true } })
      return
    }
    unchecked = true
  }
  // Removed or stopped while it was being tried: nothing is kept.
  if (!find(key) || !pending.has(key)) return
  secrets.set(key, password)
  patch(key, { lock: unchecked ? { state: 'opens', unchecked: true } : { state: 'opens' } })
  pump()
}

/** *Skip — store it locked* (FR-67): sent without a password, stored *Locked* as before. */
export function skipPassword(key: string) {
  const item = find(key)
  if (!item || !held(item)) return
  secrets.delete(key)
  patch(key, { lock: { state: 'skipped' } })
  pump()
}

/**
 * *Upload again* (FR-55, D53): a new file for a stored document whose reading
 * failed — stored as **its next version** (bytes are immutable, FR-9), never
 * a new record, and read as the first was asked, in the same transaction. A
 * refusal leaves the document as it was, and the row says why.
 */
export async function uploadAgain(key: string, file: File): Promise<void> {
  const item = find(key)
  const p = pending.get(key)
  if (!item || item.state !== 'done' || !item.document || !p || item.again?.state === 'uploading') return
  const doc = item.document
  patch(key, { again: { state: 'uploading', fileName: file.name } })
  try {
    const answer = await p.client.documents.versions.add(doc.id, { file, reason: 'replacement', parse: p.options.parse ?? undefined })
    const { job, ...stored } = answer
    const asked = Boolean(p.options.parse)
    patch(key, {
      file,
      again: undefined,
      document: stored,
      parse: !asked ? undefined : job ? { state: progressOf(job), job } : { state: 'error', message: 'It was stored, but ProDoc did not say it would read it.' },
    })
    if (asked && job) void followParse(key, stored, job, p)
  } catch (e) {
    patch(key, { again: { state: 'refused', fileName: file.name, message: errorText(e, 'The new version was not stored.') } })
  }
}

/** Keep the checks and the requests moving, within their limits. */
function pump() {
  for (const item of items.filter((i) => i.state === 'checking' && !started.has(i.key))) {
    if (checking >= CHECKING) break
    const p = pending.get(item.key)
    if (!p) continue
    // Marked before the first await so a second pump cannot start it twice.
    started.add(item.key)
    void check(item, p)
  }

  if (sending >= CONCURRENCY) return
  // W30: a file waiting for its password is not sent until it has one, or is skipped.
  const queued = items.filter((i) => i.state === 'queued' && !held(i))
  if (queued.length === 0) return
  const byOwner = new Map<string, UploadItem[]>()
  for (const item of queued) byOwner.set(item.owner, [...(byOwner.get(item.owner) ?? []), item])
  for (const [owner, group] of byOwner) {
    const p = pending.get(group[0]!.key)
    if (!p) continue
    const maxFiles = p.options.maxFiles ?? DEFAULT_MAX_FILES_PER_REQUEST
    const plan = planBatches(
      // A new version names one document; a password belongs to one file (W30).
      group.map((i) => ({ key: i.key, size: i.file.size, group: groupOf(i, pending.get(i.key)?.options ?? p.options), alone: i.choice === 'new_version' || secrets.has(i.key) })),
      { maxFiles, maxBytes: MAX_BATCH_BYTES },
    )
    // While files of this batch are still being checked, send only requests
    // that are already full — so a drop of 200 goes as ten requests of twenty,
    // not two hundred of one. The last, partial one waits for the checks.
    const stillChecking = items.some((i) => i.owner === owner && i.state === 'checking')
    const full = (keys: string[]) =>
      keys.length >= maxFiles || keys.reduce((n, k) => n + (find(k)?.file.size ?? 0), 0) >= MAX_BATCH_BYTES / 2 || find(keys[0]!)?.choice === 'new_version' || secrets.has(keys[0]!)
    for (const keys of plan) {
      if (sending >= CONCURRENCY) return
      if (stillChecking && !full(keys)) continue
      // Mark before the request starts so the next pump does not plan it again.
      for (const key of keys) {
        const item = find(key)
        if (item) item.state = 'uploading'
      }
      void send(keys)
    }
  }
}

/* --------------------------------------------------------------- the API */

/**
 * Add files to the batch. Each is checked against the organisation's own
 * limits **before** a byte is sent; the service checks them again.
 */
export function addFiles(
  client: ProDocClient,
  files: FileList | File[],
  options: AddOptions,
  placed: { folderId: string | null; folderPath?: string[]; relativePath?: string }[] = [],
) {
  const added: UploadItem[] = Array.from(files).map((file, i) => {
    counter += 1
    const expectedType = mediaTypeOf(file.name, options.mediaTypes)
    const named = placed[i] ? placed[i].folderPath : options.folderPath
    const base = {
      key: `u${counter}`,
      file,
      progress: 0,
      owner: options.owner,
      workspaceId: options.workspaceId,
      folderId: placed[i] ? placed[i].folderId : (options.folderId ?? null),
      folderPath: named?.length ? [...named] : undefined,
      relativePath: placed[i]?.relativePath,
      documentType: options.documentType,
      expectedType,
      twins: [],
      ownerApp: options.ownerApp,
    }
    if (!expectedType) {
      return { ...base, state: 'refused' as const, reason: 'unsupported_type', message: 'Not a type this organisation stores.' }
    }
    if (file.size > options.maxBytes) {
      return { ...base, state: 'refused' as const, reason: 'too_large', message: `${formatBytes(file.size)} — larger than the ${formatBytes(options.maxBytes)} limit.` }
    }
    return { ...base, state: 'checking' as const }
  })
  for (const item of added) pending.set(item.key, { client, options })
  items = [...items, ...added]
  emit()
  pump()
}

/* ------------------------------------------------------- a dropped folder */

/** FR-7's ceiling: a folder drop of up to 200 files. */
export const MAX_FOLDER_DROP = 200

/** One file of a dropped folder: the file, and the folders it sat in from the drop's root (`['a', 'b']` for `a/b/x.pdf`). */
export interface DroppedFile {
  file: File
  path: string[]
}

/** The operating system's litter in a folder — never a document, so never listed. */
const LITTER = /^(\..*|thumbs\.db|desktop\.ini)$/i

/**
 * Every folder a drop needs, each **once**, parents before children:
 * `a/b/x.pdf` and `a/y.pdf` need `a` and `a/b`.
 */
export function folderPathsOf(files: DroppedFile[]): string[][] {
  const seen = new Set<string>()
  const out: string[][] = []
  for (const { path } of files) {
    for (let depth = 1; depth <= path.length; depth += 1) {
      const prefix = path.slice(0, depth)
      const key = prefix.join('/')
      if (seen.has(key)) continue
      seen.add(key)
      out.push(prefix)
    }
  }
  return out.sort((a, b) => a.length - b.length)
}

/** A directory's entries, all of them: `readEntries` answers in batches until it answers none. */
function readAll(dir: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> {
  const reader = dir.createReader()
  const all: FileSystemEntry[] = []
  return new Promise((resolve, reject) => {
    const next = () =>
      reader.readEntries((batch) => {
        if (batch.length === 0) resolve(all)
        else {
          all.push(...batch)
          next()
        }
      }, reject)
    next()
  })
}

const fileOf = (entry: FileSystemFileEntry) => new Promise<File>((resolve, reject) => entry.file(resolve, reject))

/**
 * The files of a drop's entries (`DataTransferItem.webkitGetAsEntry()`, taken
 * in the drop event itself), each with the folders it sat in. A loose file
 * beside the folders has no path. The walk stops once it has `limit` files, so
 * a drop of thousands is refused without reading them all.
 */
export async function filesOfEntries(entries: FileSystemEntry[], limit = MAX_FOLDER_DROP + 1): Promise<DroppedFile[]> {
  const out: DroppedFile[] = []
  const walk = async (entry: FileSystemEntry, path: string[]) => {
    if (out.length >= limit || LITTER.test(entry.name)) return
    if (entry.isFile) {
      out.push({ file: await fileOf(entry as FileSystemFileEntry), path })
      return
    }
    if (entry.isDirectory) {
      for (const child of await readAll(entry as FileSystemDirectoryEntry)) {
        if (out.length >= limit) return
        await walk(child, [...path, entry.name])
      }
    }
  }
  for (const entry of entries) await walk(entry, [])
  return out
}

/**
 * Add a dropped folder (FR-7): refused whole above 200 files; otherwise its
 * folders are made once under `options.folderId` in `workspaceId` — one
 * `POST /folders/paths` for the drop — and each file joins the queue filed in
 * its own. Under a host's `folderPath` (FR-39) nothing is made here: each file
 * carries the host's path and then its own, and the service makes them, the
 * host app's. Answers why not, when not; nothing is made or sent then.
 */
export async function addDroppedFolder(
  client: ProDocClient,
  files: DroppedFile[],
  options: AddOptions & { workspaceId: string },
): Promise<{ ok: true; folders: number } | { ok: false; message: string }> {
  if (files.length > MAX_FOLDER_DROP) {
    return { ok: false, message: `A folder drop takes up to ${MAX_FOLDER_DROP} files; this one has more. Drop a smaller folder, or its subfolders one at a time.` }
  }
  if (files.length === 0) return { ok: false, message: 'That folder holds no files.' }
  const paths = folderPathsOf(files)
  const hostPath = options.folderPath ?? []
  if (hostPath.length > 0) {
    // The host named the path (FR-39): its folders are the host app's, made by
    // the service as each file is filed — `folder_path` is the host's path and
    // then the folders the file sat in, so nothing is made here first.
    addFiles(
      client,
      files.map((f) => f.file),
      options,
      files.map((f) => ({ folderId: options.folderId ?? null, folderPath: [...hostPath, ...f.path], relativePath: [...f.path, f.file.name].join('/') })),
    )
    return { ok: true, folders: paths.length }
  }
  let made: Record<string, string> = {}
  if (paths.length > 0) {
    try {
      made = await client.folders.ensurePaths({ workspace_id: options.workspaceId, parent_id: options.folderId ?? null, paths })
    } catch (e) {
      return { ok: false, message: `Its folders could not be made: ${errorText(e)} Nothing was uploaded.` }
    }
    const missing = paths.find((p) => !made[p.join('/')])
    if (missing) return { ok: false, message: `The folder “${missing.join('/')}” was not made, so nothing was uploaded.` }
  }
  addFiles(
    client,
    files.map((f) => f.file),
    options,
    files.map((f) => ({ folderId: f.path.length ? made[f.path.join('/')]! : (options.folderId ?? null), relativePath: [...f.path, f.file.name].join('/') })),
  )
  return { ok: true, folders: paths.length }
}

/**
 * The person's answer to a duplicate (FR-3): *keep both* stores a second
 * record, *new version* adds the bytes to `linkTo`, *discard* stores nothing
 * and tells the service so it is audited.
 */
export async function resolveDuplicate(key: string, choice: OnDuplicate | 'discard', linkTo?: string): Promise<void> {
  const item = find(key)
  const p = pending.get(key)
  if (!item || item.state !== 'asking' || !p) return
  if (choice === 'discard') {
    const twin = item.twins[0]
    secrets.delete(key)
    patch(key, { choice, state: 'discarded', message: undefined })
    if (twin) {
      try {
        const sha256 = item.sha256 || (await sha256Hex(item.file))
        await p.client.documents.discardDuplicate({ document_id: twin.id, sha256 })
      } catch {
        // The file is not stored either way; the audit row is the service's to
        // keep, and a failed note of a non-upload loses nothing.
      }
    }
    return
  }
  if (choice === 'new_version' && !linkTo) return
  patch(key, { choice, linkTo: choice === 'new_version' ? linkTo : undefined, state: 'queued' })
  pump()
}

/** Take a file out of the batch — before it uploads, or once it is finished. */
export function removeItem(key: string) {
  items = items.filter((i) => i.key !== key || i.state === 'uploading')
  if (!items.some((i) => i.key === key)) {
    pending.delete(key)
    secrets.delete(key)
  }
  emit()
}

/** Try again: re-send a file that failed to upload. */
export function retryItem(key: string) {
  const item = find(key)
  if (!item || item.state !== 'error') return
  patch(key, { state: 'queued', message: undefined, code: undefined, reason: undefined, limit: undefined, parseRefused: undefined, pair: undefined, progress: 0 })
  pump()
}

const FINISHED: UploadState[] = ['done', 'refused', 'error', 'discarded']

/** Clear everything that has an outcome; anything still going, or waiting for a choice, stays. */
export function clearFinished(owner: string) {
  const gone = items.filter((i) => i.owner === owner && FINISHED.includes(i.state))
  for (const i of gone) {
    pending.delete(i.key)
    secrets.delete(i.key)
  }
  items = items.filter((i) => !gone.includes(i))
  emit()
}

/** Forget every batch — for tests, and nothing else. */
export function resetUploads() {
  items = []
  pending.clear()
  secrets.clear()
  started.clear()
  seen.clear()
  dialogs.clear()
  hostOrgs.clear()
  checking = 0
  sending = 0
  emit()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** This organisation's batch. */
export function useUploads(owner: string): UploadItem[] {
  const all = useSyncExternalStore(subscribe, () => items)
  return all.filter((i) => i.owner === owner)
}

/** The whole batch, outside React. */
export const uploadsOf = (owner: string): UploadItem[] => items.filter((i) => i.owner === owner)

/* ------------------------------------------- W22: the popup, the tray, leaving */

/** Not yet stored: being checked, waiting, waiting for the person's duplicate choice, sending — or a new version on its way. */
const NOT_STORED: UploadState[] = ['checking', 'asking', 'queued', 'uploading']
const notStored = (i: UploadItem) => NOT_STORED.includes(i.state) || i.again?.state === 'uploading'
const beingRead = (i: UploadItem) => i.state === 'done' && (i.parse?.state === 'queued' || i.parse?.state === 'running')

/** A host app's files, in every organisation of this tab — or one organisation's, with `owner`. */
const ofApp = (ownerApp: string, owner?: string | null) => items.filter((i) => i.ownerApp === ownerApp && (!owner || i.owner === owner))

/**
 * How many of a host app's files are **not yet stored** (FR-59, C-W22): the
 * tab's close warns while it is above nought, and the org switch and sign-out
 * ask. A file waiting for the person's duplicate choice counts — it is not
 * stored either. A stored file still being read does not: its reading goes on
 * in the service whatever the tab does.
 */
export function uploadsInFlight(ownerApp: string, owner?: string | null): number {
  return ofApp(ownerApp, owner).filter(notStored).length
}

/**
 * Stop a host app's files that have not been sent (FR-59: going ahead with an
 * org switch or a sign-out "stops the files not yet stored; the stored ones
 * stay"): those being checked, waiting, or waiting for a choice leave the
 * batch. A request already on its way cannot be called back; what it stores
 * stays stored. Answers how many were stopped.
 */
export function stopUploads(ownerApp: string, owner?: string | null): number {
  const gone = ofApp(ownerApp, owner).filter((i) => i.state === 'checking' || i.state === 'queued' || i.state === 'asking')
  if (gone.length === 0) return 0
  for (const i of gone) {
    pending.delete(i.key)
    started.delete(i.key)
    secrets.delete(i.key)
  }
  items = items.filter((i) => !gone.includes(i))
  emit()
  return gone.length
}

/** The organisation each host app last showed its popup or uploader in. */
const hostOrgs = new Map<string, string>()

/**
 * The host's organisation, as its popup and uploader see it. When it changes
 * — the person went ahead with an org switch (FR-59) — the previous
 * organisation's files not yet sent are stopped, so none lands there after the
 * person has left it. (A client carries the org it was made with.)
 */
export function noteHostOrg(ownerApp: string, orgId: string) {
  const before = hostOrgs.get(ownerApp)
  hostOrgs.set(ownerApp, orgId)
  if (before && before !== orgId) stopUploads(ownerApp, before)
}

/** Finished files the person has seen — in the popup, or by dismissing the tray — so the tray says "done" once. */
const seen = new Set<string>()
/** The popups open, by host app: the tray steps aside while one is. */
const dialogs = new Map<string, number>()
/** Bumped with every change to `seen` and `dialogs`, which are not `items`. */
let marks = 0

function touch() {
  marks += 1
  emit()
}

/**
 * The popup opened (`true`) or closed: while open, its tray is hidden. Closed
 * with everything finished, the batch is seen; closed with a file still going,
 * the tray counts the whole batch to its end — *12 stored · 1 failed*.
 */
export function setUploadDialogOpen(ownerApp: string, open: boolean) {
  const n = dialogs.get(ownerApp) ?? 0
  if (open) dialogs.set(ownerApp, n + 1)
  else if (n <= 1) dialogs.delete(ownerApp)
  else dialogs.set(ownerApp, n - 1)
  const mine = ofApp(ownerApp)
  if (!open && !mine.some((i) => notStored(i) || beingRead(i))) for (const i of mine) seen.add(i.key)
  touch()
}

/** The tray's ×: what finished is seen, and the tray goes until the next file. */
export function dismissFinished(ownerApp: string, owner?: string | null) {
  for (const i of ofApp(ownerApp, owner)) if (!notStored(i) && !beingRead(i)) seen.add(i.key)
  touch()
}

/** The tray's line (FR-59): *4 of 12 stored*, then *12 stored · 1 failed*. `null` while it has nothing to say. */
export interface TraySummary {
  /** `uploading` while a file is not stored; `reading` while a stored one is being read; `done` once everything has an outcome. */
  phase: 'uploading' | 'reading' | 'done'
  total: number
  stored: number
  /** Not stored (refused, failed in transit), or stored and its reading failed. */
  failed: number
  /** Waiting for the person's duplicate choice. */
  waiting: number
  /** Stored and being read. */
  reading: number
  text: string
}

export function trayOf(ownerApp: string, owner?: string | null): TraySummary | null {
  if ((dialogs.get(ownerApp) ?? 0) > 0) return null
  // What the person has not seen finish: a batch already seen in the popup is not counted again.
  const all = ofApp(ownerApp, owner).filter((i) => i.state !== 'discarded' && (!seen.has(i.key) || notStored(i) || beingRead(i)))
  if (all.length === 0) return null
  const unsent = all.filter(notStored).length
  const reading = all.filter(beingRead).length
  const stored = all.filter((i) => i.state === 'done').length
  // Waiting for the person: a duplicate's choice, or a file's password (W30).
  const waiting = all.filter((i) => i.state === 'asking' || (i.state === 'queued' && held(i))).length
  const failed = all.filter((i) => i.state === 'refused' || i.state === 'error' || readingOfItem(i)?.state === 'failed').length
  const phase = unsent > 0 ? 'uploading' : reading > 0 ? 'reading' : 'done'
  const parts =
    phase === 'uploading'
      ? [`${stored} of ${all.length} stored`, waiting ? `${waiting} waiting for your choice` : null]
      : [`${stored} stored`, reading ? `${reading} being read` : null, failed ? `${failed} failed` : null]
  return { phase, total: all.length, stored, failed, waiting, reading, text: parts.filter(Boolean).join(' · ') }
}

/** The tray's line, re-read with every change to the batch. */
export function useTray(ownerApp: string, owner?: string | null): TraySummary | null {
  useSyncExternalStore(subscribe, () => items)
  useSyncExternalStore(subscribe, () => marks)
  return trayOf(ownerApp, owner)
}

/** `uploadsInFlight`, re-read with every change to the batch. */
export function useUploadsInFlight(ownerApp: string, owner?: string | null): number {
  useSyncExternalStore(subscribe, () => items)
  return uploadsInFlight(ownerApp, owner)
}
